import { useState } from "react";
import { Loader2 } from "lucide-react";
import { createAccount, startOnboardingAndWait } from "../api/api";
import VncModal from "../components/VncModal";
import useCTraderOnboarding from "../hooks/useCTraderOnboarding";
import CTraderOnboardingModal from "../components/CTraderOnboardingModal";
import { Input } from "../components/ui/input";

const formatErrorValue = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.map(formatErrorValue).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, nestedValue]) => {
        const nestedMessage = formatErrorValue(nestedValue);
        return nestedMessage ? `${key}: ${nestedMessage}` : "";
      })
      .filter(Boolean)
      .join(". ");
  }
  return String(value);
};

const parseCreateAccountError = (error) => {
  const data = error?.data || error?.response?.data || null;
  const accountNumberError = formatErrorValue(data?.credentials?.account_number);
  const message =
    accountNumberError ||
    formatErrorValue(data?.detail) ||
    formatErrorValue(data) ||
    error?.message ||
    "Failed to create account.";

  return {
    message,
    fieldErrors: accountNumberError ? { account_number: accountNumberError } : {},
  };
};

const CreateAccountModal = ({
  closeModal,
  refreshAccounts,
  availableAccounts,
  onSelect,
  initialPlatform = "MT5",
  mode = "standard",
}) => {
  const [newAccount, setNewAccount] = useState({
    name: "",
    platform: initialPlatform,
    account_number: "",
    password: "",
    server: "",
    ib_username: "",
    ib_password: "",
    ib_account_id: "",
    ib_environment: "PAPER",
    ig_username: "",
    ig_password: "",
    ig_api_key: "",
    ig_account_id: "",
    ig_environment: "DEMO",
  });
  const [vncDetails, setVncDetails] = useState(null);
  const [internalAccountId, setInternalAccountId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const ctraderOnboarding = useCTraderOnboarding();
  const [showCTraderModal, setShowCTraderModal] = useState(false);
  const modalTitle = mode === "onboarding" ? "Connect your first trading account" : "Add New Account";
  const modalDescription = mode === "onboarding"
    ? "Choose a platform, enter the required details, and we will guide you through the rest."
    : null;
  const submitLabel = mode === "onboarding" ? "Continue" : "Save";
  const pendingSubmitLabel = newAccount.platform === "MT5"
    ? "Starting MetaTrader..."
    : mode === "onboarding"
      ? "Continuing..."
      : "Saving...";

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError("");
    setFieldErrors({});

    const accountData = { name: newAccount.name, platform: newAccount.platform };

    if (newAccount.platform === "MT5") {
      accountData.credentials = {
        account_number: newAccount.account_number,
        password: newAccount.password,
        broker_server: newAccount.server,
      };
    } else if (newAccount.platform === "InteractiveBrokers") {
      accountData.credentials = {
        ib_username: newAccount.ib_username,
        ib_password: newAccount.ib_password,
        ib_account_id: newAccount.ib_account_id,
        ib_environment: newAccount.ib_environment,
      };
    } else if (newAccount.platform === "IG") {
      accountData.credentials = {
        ig_username: newAccount.ig_username,
        ig_password: newAccount.ig_password,
        ig_api_key: newAccount.ig_api_key,
        ig_account_id: newAccount.ig_account_id,
        ig_environment: newAccount.ig_environment,
      };
    }

    try {
      const createdAccount = await createAccount(accountData);
      if (newAccount.platform === "MT5") {
        setInternalAccountId(createdAccount.account_id);
        const onboardData = {
          internal_account_id: createdAccount.account_id,
        };
        const vncData = await startOnboardingAndWait(onboardData);
        if (vncData?.vnc_details) {
          setVncDetails(vncData.vnc_details);
        } else {
          alert("Failed to start MetaTrader session.");
          setIsSubmitting(false);
        }
      } else if (newAccount.platform === "cTrader") {
        setInternalAccountId(createdAccount.account_id);
        setShowCTraderModal(true);
        const res = await ctraderOnboarding.start(createdAccount.account_id);
        if (!res?.ok && res?.error) {
          console.error("cTrader onboarding start failed:", res.error);
        }
        setIsSubmitting(false);
      } else {
        // InteractiveBrokers and other platforms — no special onboarding
        refreshAccounts();
        closeModal();
      }
    } catch (err) {
      console.error("Error creating account:", err);
      const parsedError = parseCreateAccountError(err);
      setFormError(parsedError.message);
      setFieldErrors(parsedError.fieldErrors);
      setIsSubmitting(false);
    }
  };

  const handleCTraderSelect = async (acc) => {
    if (!acc) return;
    if (!internalAccountId) {
      console.error("No internalAccountId set for cTrader onboarding.");
      alert("Couldn't proceed: missing internal account id. Please close and try again.");
      return;
    }
    const ctrader_account_id = acc?.ctidTraderAccountId || acc?.ctrader_account_id || acc?.accountId || acc?.id;
    const envRaw = (acc?.environment || acc?.type || acc?.accountType || "").toString().toUpperCase();
    const environment = (envRaw.includes("LIVE") || envRaw.includes("REAL") || envRaw.includes("PROD")) ? "LIVE" : "DEMO";
    try {
      const result = await ctraderOnboarding.chooseAccount(internalAccountId, { ctrader_account_id, environment });
      if (result?.ok) {
        try { await refreshAccounts(); } catch (_) {}
        closeModal();
      } else if (result?.error) {
        console.error("Failed to complete cTrader onboarding:", result.error);
        alert(result.error?.message || "Failed to link cTrader account.");
      }
    } catch (err) {
      console.error("Error selecting cTrader account:", err);
      alert(err?.message || "Unexpected error while linking cTrader account.");
    }
  };

  const handleCTraderClose = () => {
    ctraderOnboarding.cancel();
    setShowCTraderModal(false);
  };

  const handleCTraderRetry = async () => {
    if (!internalAccountId) return;
    await ctraderOnboarding.start(internalAccountId);
  };

  if (vncDetails) {
    return (
      <VncModal
        vncDetails={vncDetails}
        internalAccountId={internalAccountId}
        closeModal={closeModal}
        refreshAccounts={refreshAccounts}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-textPrimary/50 flex justify-center items-center p-4 z-50">
      <div className="bg-backgroundSurface text-textPrimary border border-borderDefault p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-[340px] sm:max-w-[384px]">
        <h3 className="text-lg sm:text-xl font-semibold mb-2">{modalTitle}</h3>
        {modalDescription && (
          <p className="text-sm text-textSecondary mb-3 sm:mb-4">{modalDescription}</p>
        )}

        <form onSubmit={handleCreateAccount}>
          {formError && (
            <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {formError}
            </div>
          )}

          <Input
            type="text"
            placeholder="Account Name"
            className="mb-2"
            value={newAccount.name}
            onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
            required
          />

          <select
            className="w-full px-3 py-2 rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary placeholder:text-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mb-2"
            value={newAccount.platform}
            onChange={(e) => setNewAccount({ ...newAccount, platform: e.target.value })}
          >
            <option value="MT5">MT5</option>
            <option value="cTrader">cTrader</option>
            <option value="InteractiveBrokers">Interactive Brokers</option>
            <option value="IG">IG</option>
          </select>

          {newAccount.platform === "MT5" && (
            <>
              <Input
                type="text"
                placeholder="Account Number"
                className={`mb-1 ${fieldErrors.account_number ? "border-destructive focus:border-destructive focus:ring-destructive" : ""}`}
                value={newAccount.account_number}
                onChange={(e) => {
                  setNewAccount({ ...newAccount, account_number: e.target.value });
                  setFormError("");
                  setFieldErrors((current) => ({ ...current, account_number: "" }));
                }}
                aria-invalid={fieldErrors.account_number ? "true" : "false"}
                required
              />
              {fieldErrors.account_number && (
                <p className="mb-2 text-xs text-destructive">{fieldErrors.account_number}</p>
              )}
              <Input
                type="password"
                placeholder="Password"
                className="mb-2"
                value={newAccount.password}
                onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                required
              />
              <Input
                type="text"
                placeholder="Broker Server"
                className="mb-2"
                value={newAccount.server}
                onChange={(e) => setNewAccount({ ...newAccount, server: e.target.value })}
                required
              />
            </>
          )}

          {newAccount.platform === "InteractiveBrokers" && (
            <>
              <Input
                type="text"
                placeholder="IB Username"
                className="mb-2"
                value={newAccount.ib_username}
                onChange={(e) => setNewAccount({ ...newAccount, ib_username: e.target.value })}
                required
              />
              <Input
                type="password"
                placeholder="IB Password"
                className="mb-2"
                value={newAccount.ib_password}
                onChange={(e) => setNewAccount({ ...newAccount, ib_password: e.target.value })}
                required
              />
              <Input
                type="text"
                placeholder="IB Account ID (e.g. DU1234567)"
                className="mb-2"
                value={newAccount.ib_account_id}
                onChange={(e) => setNewAccount({ ...newAccount, ib_account_id: e.target.value })}
                required
              />
              <select
                className="w-full px-3 py-2 rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mb-2"
                value={newAccount.ib_environment}
                onChange={(e) => setNewAccount({ ...newAccount, ib_environment: e.target.value })}
              >
                <option value="PAPER">Paper Trading</option>
                <option value="LIVE">Live Trading</option>
              </select>
            </>
          )}

          {newAccount.platform === "IG" && (
            <>
              <Input
                type="text"
                placeholder="IG Username"
                className="mb-2"
                value={newAccount.ig_username}
                onChange={(e) => setNewAccount({ ...newAccount, ig_username: e.target.value })}
                required
              />
              <Input
                type="password"
                placeholder="IG Password"
                className="mb-2"
                value={newAccount.ig_password}
                onChange={(e) => setNewAccount({ ...newAccount, ig_password: e.target.value })}
                required
              />
              <Input
                type="password"
                placeholder="IG API Key"
                className="mb-2"
                value={newAccount.ig_api_key}
                onChange={(e) => setNewAccount({ ...newAccount, ig_api_key: e.target.value })}
                required
              />
              <Input
                type="text"
                placeholder="IG Account ID (optional)"
                className="mb-2"
                value={newAccount.ig_account_id}
                onChange={(e) => setNewAccount({ ...newAccount, ig_account_id: e.target.value })}
              />
              <select
                className="w-full px-3 py-2 rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mb-2"
                value={newAccount.ig_environment}
                onChange={(e) => setNewAccount({ ...newAccount, ig_environment: e.target.value })}
              >
                <option value="DEMO">Demo</option>
                <option value="LIVE">Live</option>
              </select>
            </>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeModal}
              disabled={isSubmitting}
              className="btn btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="btn btn-primary inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>{pendingSubmitLabel}</span>
                </>
              ) : (
                submitLabel
              )}
            </button>
          </div>
        </form>
      </div>

      <CTraderOnboardingModal
        open={showCTraderModal}
        onClose={handleCTraderClose}
        loading={ctraderOnboarding.status === "loadingAccounts" || ctraderOnboarding.status === "completing"}
        error={ctraderOnboarding.error}
        accounts={ctraderOnboarding.accounts}
        status={ctraderOnboarding.status}
        onSelect={handleCTraderSelect}
        onRetry={handleCTraderRetry}
      />
    </div>
  );
};

export default CreateAccountModal;
